import React, { useEffect, useState } from "react";
import { ActivityIndicator, Alert, StyleSheet, Text, View } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import {
  Button,
  Card,
  SegmentedButtons,
  Switch,
  TextInput,
  Snackbar,
} from "react-native-paper";
import {
  configService,
  ConfigurationDocument,
} from "../../services/configService";
import {
  Collection,
  Config,
  Field,
  FieldType,
  Filter,
  Screen,
} from "../../config/companies/config";

interface ConfigEditorScreenProps {
  navigation: any;
  route: {
    params: {
      configId?: string;
    };
  };
}

const FIELD_TYPES: FieldType[] = [
  "text",
  "email",
  "url",
  "password",
  "phone",
  "textarea",
  "dropdown",
  "numberId",
  "collectionPicker",
  "mechanicPicker",
  "testPicker",
  "paymentsForm",
  "calculated",
  "status",
  "datePicker",
  "file",
  "document",
  "number",
  "otp",
  "signature",
  "rating",
  "color",
  "location",
  "reference",
  "dateTime",
  "segmentedButtons",
  "booleanCheckbox",
];

export default function ConfigEditorScreen({
  navigation,
  route,
}: ConfigEditorScreenProps) {
  const { configId } = route.params || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<ConfigurationDocument | null>(null);
  const [configData, setConfigData] = useState<Config>({
    appName: "",
    mainCollection: "",
    theme: {
      primaryColor: "#3498db",
      secondaryColor: "#2ecc71",
    },
    roles: ["admin"],
    defaultRole: "admin",
    statuses: ["pending", "in progress", "completed"],
    employeesVisibleTo: ["admin"],
    employeesEditableBy: ["admin"],
    screens: [],
    database: {
      collections: [],
    },
  });
  const [snackbar, setSnackbar] = useState({ visible: false, message: "" });

  useEffect(() => {
    if (configId) {
      loadConfiguration();
    } else {
      setLoading(false);
    }
  }, [configId]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      if (configId) {
        const loadedConfig = await configService.getConfigurationById(configId);
        if (loadedConfig) {
          setConfig(loadedConfig);
          setConfigData(loadedConfig.config);
        }
      }
    } catch (error) {
      console.error("Error loading configuration:", error);
      setSnackbar({ visible: true, message: "Failed to load configuration" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (!configData.appName || !configData.mainCollection) {
        setSnackbar({
          visible: true,
          message: "App name and main collection are required",
        });
        return;
      }

      if (config) {
        // Update existing configuration
        await configService.updateConfiguration(config.id, {
          config: configData,
        });
        setSnackbar({
          visible: true,
          message: "Configuration updated successfully",
        });
      } else {
        setSnackbar({
          visible: true,
          message: "Cannot save without a configuration ID",
        });
      }
    } catch (error) {
      console.error("Error saving configuration:", error);
      setSnackbar({ visible: true, message: "Failed to save configuration" });
    } finally {
      setSaving(false);
    }
  };

  const addRole = () => {
    const newRole = `role${configData.roles.length + 1}`;
    setConfigData({
      ...configData,
      roles: [...configData.roles, newRole],
    });
  };

  const removeRole = (index: number) => {
    const newRoles = configData.roles.filter((_, i) => i !== index);
    setConfigData({
      ...configData,
      roles: newRoles,
    });
  };

  const addScreen = () => {
    const newScreen: Screen = {
      name: `Screen${configData.screens.length + 1}`,
      type: "board",
      collection: configData.mainCollection,
      roles: configData.roles,
      defaultPresentation: "horizontal",
    };
    setConfigData({
      ...configData,
      screens: [...configData.screens, newScreen],
    });
  };

  const removeScreen = (index: number) => {
    const newScreens = configData.screens.filter((_, i) => i !== index);
    setConfigData({
      ...configData,
      screens: newScreens,
    });
  };

  const addCollection = () => {
    const newCollection: Collection = {
      name: `collection${configData.database.length + 1}`,
      fields: [],
      deletableBy: ["admin"],
    };
    setConfigData({
      ...configData,
      database: [...configData.database, newCollection],
    });
  };

  const removeCollection = (index: number) => {
    const newCollections = configData.database.filter((_, i) => i !== index);
    setConfigData({
      ...configData,
      database: newCollections,
    });
  };

  const addField = (collectionIndex: number) => {
    const newField: Field = {
      name: `field${configData.database[collectionIndex].fields.length + 1}`,
      type: "text",
      label: "New Field",
      list: configData.roles,
      formCreate: configData.roles,
      formEdit: configData.roles,
      board: configData.roles,
      required: false,
    };

    const updatedCollections = [...configData.database];
    updatedCollections[collectionIndex] = {
      ...updatedCollections[collectionIndex],
      fields: [...updatedCollections[collectionIndex].fields, newField],
    };

    setConfigData({
      ...configData,
      database: updatedCollections,
    });
  };

  const removeField = (collectionIndex: number, fieldIndex: number) => {
    const updatedCollections = [...configData.database];
    updatedCollections[collectionIndex] = {
      ...updatedCollections[collectionIndex],
      fields: updatedCollections[collectionIndex].fields.filter(
        (_, i) => i !== fieldIndex,
      ),
    };

    setConfigData({
      ...configData,
      database: updatedCollections,
    });
  };

  const updateField = (
    collectionIndex: number,
    fieldIndex: number,
    updates: Partial<Field>,
  ) => {
    const updatedCollections = [...configData.database];
    updatedCollections[collectionIndex] = {
      ...updatedCollections[collectionIndex],
      fields: updatedCollections[collectionIndex].fields.map((field, i) =>
        i === fieldIndex ? { ...field, ...updates } : field,
      ),
    };

    setConfigData({
      ...configData,
      database: updatedCollections,
    });
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
        <Text>Loading configuration...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <KeyboardAwareScrollView
        style={styles.scrollView}
        bottomOffset={20}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <Text style={styles.title}>Configuration Editor</Text>
          <Text style={styles.subtitle}>
            {config ? `Editing: ${config.name}` : "Create new configuration"}
          </Text>
        </View>

        {/* Basic Settings */}
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>Basic Settings</Text>
            <TextInput
              label="App Name"
              value={configData.appName}
              onChangeText={(text) =>
                setConfigData({ ...configData, appName: text })
              }
              style={styles.input}
              mode="outlined"
            />
            <TextInput
              label="Main Collection"
              value={configData.mainCollection}
              onChangeText={(text) =>
                setConfigData({ ...configData, mainCollection: text })
              }
              style={styles.input}
              mode="outlined"
            />
            <TextInput
              label="Primary Color"
              value={configData.theme.primaryColor}
              onChangeText={(text) =>
                setConfigData({
                  ...configData,
                  theme: { ...configData.theme, primaryColor: text },
                })
              }
              style={styles.input}
              mode="outlined"
            />
            <TextInput
              label="Secondary Color"
              value={configData.theme.secondaryColor}
              onChangeText={(text) =>
                setConfigData({
                  ...configData,
                  theme: { ...configData.theme, secondaryColor: text },
                })
              }
              style={styles.input}
              mode="outlined"
            />
          </Card.Content>
        </Card>

        {/* Roles */}
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Roles</Text>
              <Button mode="outlined" onPress={addRole} icon="plus">
                Add Role
              </Button>
            </View>
            {configData.roles.map((role, index) => (
              <View key={index} style={styles.roleItem}>
                <TextInput
                  value={role}
                  onChangeText={(text) => {
                    const newRoles = [...configData.roles];
                    newRoles[index] = text;
                    setConfigData({ ...configData, roles: newRoles });
                  }}
                  style={styles.roleInput}
                  mode="outlined"
                />
                <Button
                  mode="outlined"
                  onPress={() => removeRole(index)}
                  icon="delete"
                  textColor="red"
                >
                  Remove
                </Button>
              </View>
            ))}
          </Card.Content>
        </Card>

        {/* Screens */}
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Screens</Text>
              <Button
                mode="outlined"
                onPress={() => {
                  const newScreen: Screen = {
                    name: `Screen${configData.screens.length + 1}`,
                    type: "board",
                    collection: configData.mainCollection,
                    roles: configData.roles,
                    defaultPresentation: "horizontal",
                  };
                  setConfigData({
                    ...configData,
                    screens: [...configData.screens, newScreen],
                  });
                }}
                icon="plus"
              >
                Add Screen
              </Button>
            </View>
            {configData.screens.map((screen, screenIndex) => (
              <View key={screenIndex} style={styles.collectionContainer}>
                <View style={styles.collectionHeader}>
                  <TextInput
                    label="Screen Name"
                    value={screen.name}
                    onChangeText={(text) => {
                      const updatedScreens = [...configData.screens];
                      updatedScreens[screenIndex] = {
                        ...updatedScreens[screenIndex],
                        name: text,
                      };
                      setConfigData({ ...configData, screens: updatedScreens });
                    }}
                    style={styles.collectionNameInput}
                    mode="outlined"
                  />
                  <Button
                    mode="outlined"
                    onPress={() => {
                      const updatedScreens = configData.screens.filter(
                        (_, i) => i !== screenIndex,
                      );
                      setConfigData({ ...configData, screens: updatedScreens });
                    }}
                    icon="delete"
                    textColor="red"
                  >
                    Remove
                  </Button>
                </View>
                <TextInput
                  label="Type (board, list, chat, reports, etc)"
                  value={screen.type}
                  onChangeText={(text) => {
                    const updatedScreens = [...configData.screens];
                    updatedScreens[screenIndex] = {
                      ...updatedScreens[screenIndex],
                      type: text as any,
                    };
                    setConfigData({ ...configData, screens: updatedScreens });
                  }}
                  style={styles.input}
                  mode="outlined"
                />
                <TextInput
                  label="Collection"
                  value={screen.collection}
                  onChangeText={(text) => {
                    const updatedScreens = [...configData.screens];
                    updatedScreens[screenIndex] = {
                      ...updatedScreens[screenIndex],
                      collection: text,
                    };
                    setConfigData({ ...configData, screens: updatedScreens });
                  }}
                  style={styles.input}
                  mode="outlined"
                />
                <TextInput
                  label="Roles (comma separated)"
                  value={screen.roles.join(", ")}
                  onChangeText={(text) => {
                    const updatedScreens = [...configData.screens];
                    updatedScreens[screenIndex] = {
                      ...updatedScreens[screenIndex],
                      roles: text.split(",").map((r) => r.trim()),
                    };
                    setConfigData({ ...configData, screens: updatedScreens });
                  }}
                  style={styles.input}
                  mode="outlined"
                />
                <TextInput
                  label="Icon (optional)"
                  value={screen.icon || ""}
                  onChangeText={(text) => {
                    const updatedScreens = [...configData.screens];
                    updatedScreens[screenIndex] = {
                      ...updatedScreens[screenIndex],
                      icon: text,
                    };
                    setConfigData({ ...configData, screens: updatedScreens });
                  }}
                  style={styles.input}
                  mode="outlined"
                />
                <TextInput
                  label="Default Presentation (horizontal, table, etc)"
                  value={screen.defaultPresentation || ""}
                  onChangeText={(text) => {
                    const updatedScreens = [...configData.screens];
                    updatedScreens[screenIndex] = {
                      ...updatedScreens[screenIndex],
                      defaultPresentation: text as any,
                    };
                    setConfigData({ ...configData, screens: updatedScreens });
                  }}
                  style={styles.input}
                  mode="outlined"
                />
                <TextInput
                  label="Order By (JSON array of {field,direction})"
                  value={screen.orderBy ? JSON.stringify(screen.orderBy) : ""}
                  onChangeText={(text) => {
                    const updatedScreens = [...configData.screens];
                    try {
                      const parsed = text ? JSON.parse(text) : undefined;
                      if (parsed && !Array.isArray(parsed))
                        throw new Error("orderBy must be an array");
                      updatedScreens[screenIndex] = {
                        ...updatedScreens[screenIndex],
                        orderBy: parsed,
                      };
                      setConfigData({ ...configData, screens: updatedScreens });
                    } catch (e) {
                      // keep previous value if invalid JSON
                      console.warn("Invalid orderBy JSON", e);
                    }
                  }}
                  style={styles.input}
                  mode="outlined"
                />
                {/* Filters editing (simple) */}
                <View style={{ marginBottom: 12 }}>
                  <Text style={{ fontWeight: "bold", marginBottom: 4 }}>
                    Filters
                  </Text>
                  {(screen.filters || []).map((filter, filterIndex) => (
                    <View key={filterIndex} style={{ marginBottom: 8 }}>
                      <TextInput
                        label="Field"
                        value={filter.field}
                        onChangeText={(text) => {
                          const updatedScreens = [...configData.screens];
                          const updatedFilters = [...(screen.filters || [])];
                          updatedFilters[filterIndex] = {
                            ...updatedFilters[filterIndex],
                            field: text,
                          };
                          updatedScreens[screenIndex] = {
                            ...updatedScreens[screenIndex],
                            filters: updatedFilters,
                          };
                          setConfigData({
                            ...configData,
                            screens: updatedScreens,
                          });
                        }}
                        style={styles.input}
                        mode="outlined"
                      />
                      <SegmentedButtons
                        value={filter.type}
                        onValueChange={(value) => {
                          const updatedScreens = [...configData.screens];
                          const updatedFilters = [...(screen.filters || [])];
                          updatedFilters[filterIndex] = {
                            ...updatedFilters[filterIndex],
                            type: value as "search" | "date",
                          };
                          updatedScreens[screenIndex] = {
                            ...updatedScreens[screenIndex],
                            filters: updatedFilters,
                          };
                          setConfigData({
                            ...configData,
                            screens: updatedScreens,
                          });
                        }}
                        buttons={[
                          { value: "search", label: "search" },
                          { value: "date", label: "date" },
                        ]}
                        style={styles.fieldTypeButtons}
                      />
                      <TextInput
                        label="Label"
                        value={filter.label}
                        onChangeText={(text) => {
                          const updatedScreens = [...configData.screens];
                          const updatedFilters = [...(screen.filters || [])];
                          updatedFilters[filterIndex] = {
                            ...updatedFilters[filterIndex],
                            label: text,
                          };
                          updatedScreens[screenIndex] = {
                            ...updatedScreens[screenIndex],
                            filters: updatedFilters,
                          };
                          setConfigData({
                            ...configData,
                            screens: updatedScreens,
                          });
                        }}
                        style={styles.input}
                        mode="outlined"
                      />
                      <Button
                        mode="outlined"
                        onPress={() => {
                          const updatedScreens = [...configData.screens];
                          const updatedFilters = [...(screen.filters || [])];
                          updatedFilters.splice(filterIndex, 1);
                          updatedScreens[screenIndex] = {
                            ...updatedScreens[screenIndex],
                            filters: updatedFilters,
                          };
                          setConfigData({
                            ...configData,
                            screens: updatedScreens,
                          });
                        }}
                        icon="delete"
                        textColor="red"
                        style={{ marginTop: 4 }}
                      >
                        Remove Filter
                      </Button>
                    </View>
                  ))}
                  <Button
                    mode="outlined"
                    onPress={() => {
                      const updatedScreens = [...configData.screens];
                      const updatedFilters = [
                        ...(screen.filters || []),
                        { field: "", type: "search", label: "" } as Filter,
                      ];
                      updatedScreens[screenIndex] = {
                        ...updatedScreens[screenIndex],
                        filters: updatedFilters,
                      };
                      setConfigData({ ...configData, screens: updatedScreens });
                    }}
                    icon="plus"
                  >
                    Add Filter
                  </Button>
                </View>
              </View>
            ))}
          </Card.Content>
        </Card>

        {/* Collections */}
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Collections</Text>
              <Button mode="outlined" onPress={addCollection} icon="plus">
                Add Collection
              </Button>
            </View>
            {configData.database.map((collection, collectionIndex) => (
              <View key={collectionIndex} style={styles.collectionContainer}>
                <View style={styles.collectionHeader}>
                  <TextInput
                    label="Collection Name"
                    value={collection.name}
                    onChangeText={(text) => {
                      const updatedCollections = [...configData.database];
                      updatedCollections[collectionIndex] = {
                        ...updatedCollections[collectionIndex],
                        name: text,
                      };
                      setConfigData({
                        ...configData,
                        database: updatedCollections,
                      });
                    }}
                    style={styles.collectionNameInput}
                    mode="outlined"
                  />
                  <Button
                    mode="outlined"
                    onPress={() => removeCollection(collectionIndex)}
                    icon="delete"
                    textColor="red"
                  >
                    Remove
                  </Button>
                </View>

                <View style={styles.fieldsSection}>
                  <View style={styles.fieldsHeader}>
                    <Text style={styles.fieldsTitle}>Fields</Text>
                    <Button
                      mode="outlined"
                      onPress={() => addField(collectionIndex)}
                      icon="plus"
                    >
                      Add Field
                    </Button>
                  </View>

                  {collection.fields.map((field, fieldIndex) => (
                    <View key={fieldIndex} style={styles.fieldContainer}>
                      <View style={styles.fieldHeader}>
                        <Text style={styles.fieldTitle}>
                          Field {fieldIndex + 1}
                        </Text>
                        <Button
                          mode="outlined"
                          onPress={() =>
                            removeField(collectionIndex, fieldIndex)
                          }
                          icon="delete"
                          textColor="red"
                        >
                          Remove
                        </Button>
                      </View>

                      <TextInput
                        label="Field Name"
                        value={field.name}
                        onChangeText={(text) =>
                          updateField(collectionIndex, fieldIndex, {
                            name: text,
                          })
                        }
                        style={styles.fieldInput}
                        mode="outlined"
                      />

                      <TextInput
                        label="Label"
                        value={field.label}
                        onChangeText={(text) =>
                          updateField(collectionIndex, fieldIndex, {
                            label: text,
                          })
                        }
                        style={styles.fieldInput}
                        mode="outlined"
                      />

                      <SegmentedButtons
                        value={field.type}
                        onValueChange={(value) =>
                          updateField(collectionIndex, fieldIndex, {
                            type: value as FieldType,
                          })
                        }
                        buttons={FIELD_TYPES.slice(0, 10).map((type) => ({
                          value: type,
                          label: type,
                        }))}
                        style={styles.fieldTypeButtons}
                      />

                      <View style={styles.fieldOptions}>
                        <Switch
                          value={field.required || false}
                          onValueChange={(value) =>
                            updateField(collectionIndex, fieldIndex, {
                              required: value,
                            })
                          }
                        />
                        <Text>Required</Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </Card.Content>
        </Card>
      </KeyboardAwareScrollView>

      <View style={styles.footer}>
        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}
        >
          Save Configuration
        </Button>
      </View>
      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({ ...snackbar, visible: false })}
        duration={3000}
      >
        {snackbar.message}
      </Snackbar>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  scrollView: {
    flex: 1,
  },
  header: {
    padding: 16,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 16,
    color: "#666",
  },
  card: {
    margin: 16,
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 16,
  },
  input: {
    marginBottom: 16,
  },
  roleItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  roleInput: {
    flex: 1,
    marginRight: 8,
  },
  collectionContainer: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#f9f9f9",
    borderRadius: 8,
  },
  collectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  collectionNameInput: {
    flex: 1,
    marginRight: 8,
  },
  fieldsSection: {
    marginTop: 16,
  },
  fieldsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  fieldsTitle: {
    fontSize: 16,
    fontWeight: "bold",
  },
  fieldContainer: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: "#fff",
    borderRadius: 6,
  },
  fieldHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  fieldTitle: {
    fontSize: 14,
    fontWeight: "bold",
  },
  fieldInput: {
    marginBottom: 12,
  },
  fieldTypeButtons: {
    marginBottom: 12,
  },
  fieldOptions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  footer: {
    padding: 16,
    backgroundColor: "#fff",
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
  },
  saveButton: {
    marginTop: 8,
  },
});
